import { ContactDeepLinkRedirect } from '@/components/contact-deeplink/contact-deeplink-redirect';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Deep-link route that opens a contact's conversation, with an optional `body`
 * query param that pre-fills the SMS composer.
 *
 * - Desktop: resolves the contact's number and redirects to the inbox with the
 *   conversation thread open. When `body` is present, the composer is seeded.
 * - Web: first attempts to launch the desktop app via the `allo://` custom
 *   protocol (preserving query params); whether or not that succeeds, also
 *   redirects to the inbox so the browser tab shows the same destination as
 *   a fallback.
 *
 * The orchestration lives in `ContactDeepLinkRedirect` so it can be unit
 * tested without the router/route context.
 *
 * Customer-facing URLs:
 * - `https://web.withallo.com/contact/<id>` (works in any browser)
 * - `https://web.withallo.com/contact/<id>?body=Bonjour%20Maxime` (pre-filled SMS)
 * - `allo://contact/<id>` (custom-protocol form, used by integrations)
 * - `allo://contact/<id>?body=Bonjour%20Maxime` (custom-protocol with body)
 */
export const Route = createFileRoute('/_app/contact/$contactId')({
  component: RouteComponent,
  validateSearch: z.object({
    body: z.string().optional(),
  }),
});

function RouteComponent() {
  const { contactId } = Route.useParams();
  const { body } = Route.useSearch();

  return <ContactDeepLinkRedirect contactId={contactId} body={body} />;
}
